import React from 'react';
import { connect, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import PersonalInformationAccordionItem from '../../components/authentication/PersonalInformationAccordionItem';
import AccountInformation from '../../components/authentication/AccountInformation';
import { apiClient } from '../../../helpers/apiHelperV2';
import { signup } from '../../../modules/actions/user';
import { useLocation } from 'react-router-dom';
import { parseSearch } from '../../../helpers/UrlHelper';
import { trackEvent } from '../../components/Tracking/Matomo';
import { standortUrlSelector } from '../../../modules/selectors/url';
import Button from '../../components/form-elements/Button';
import { useStandortHistory } from '../../components/VHost/StandortHistory';

const SignupSchema = Yup.object().shape({
    isCompany: Yup.boolean(),
    company: Yup.string().when('isCompany', {
        is: true,
        then: Yup.string().required('Pflichtfeld'),
    }),
    salutation: Yup.string().required('Pflichtfeld'),
    titel: Yup.string(),
    firstName: Yup.string().required('Pflichtfeld'),
    lastName: Yup.string().required('Pflichtfeld'),
    street: Yup.string().required('Pflichtfeld'),
    additionalAddress: Yup.string(),
    zipCode: Yup.string()
        .required('Pflichtfeld')
        .length(5, 'Bitte geben Sie Ihre fünfstellige PLZ ein.')
        .test('zipCode', 'Bitte geben Sie eine gültige deutsche PLZ ein.', async function (zipCode) {
            if (zipCode && zipCode.length === 5) {
                try {
                    const {
                        data: { isValid },
                    } = await apiClient.post('/api/v1/validation/zipCode', { zipCode });
                    return isValid;
                } catch (err) {
                    console.error(err);
                    return false;
                }
            }
        }),
    city: Yup.string().required('Pflichtfeld'),
    country: Yup.string(),
    phoneNumber: Yup.string(),
    email: Yup.string()
        .email('Pflichtfeld')
        .required('Pflichtfeld')
        .test('email', 'E-Mail-Adresse ist bereits vergeben.', async function (email) {
            if (email) {
                const standortUrl = this.parent.standortUrl;
                try {
                    const {
                        data: { isValid },
                    } = await apiClient.post('/api/v1/validation/email', { email, standortUrl });
                    return isValid;
                } catch (err) {
                    console.error(err);
                    return false;
                }
            }
        }),
    password: Yup.string().required('Pflichtfeld'),
    confirmPassword: Yup.string()
        .required('Pflichtfeld')
        .test('confirmPassword', 'Passwörter stimmen nicht überein', function (value) {
            return this.parent.password === value;
        }),
    terms: Yup.boolean().test('terms', 'Pflichtfeld', function (terms) {
        return terms;
    }),
    privacy: Yup.boolean().test('privacy', 'Pflichtfeld', function (privacy) {
        return privacy;
    }),
});

const InitialValues = {
    isCompany: false,
    company: '',
    salutation: '',
    titel: '',
    firstName: '',
    lastName: '',
    street: '',
    additionalAddress: '',
    zipCode: '',
    city: '',
    country: 'Deutschland',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    terms: '',
    privacy: '',
    standortUrl: '',
};

function RegisterForm({ signup }) {
    const location = useLocation();
    const { context, type } = parseSearch(location?.search);
    const history = useStandortHistory();
    const standortUrl = useSelector(standortUrlSelector);

    const handleSignupSubmit = async (
        {
            isCompany,
            company,
            salutation,
            titel,
            firstName,
            lastName,
            street,
            additionalAddress,
            zipCode,
            city,
            country,
            phoneNumber,
            email,
            password,
            standortUrl,
        },
        actions
    ) => {
        trackEvent({ category: 'Account', action: 'Submit-Registrieren' });
        const user = {
            isCompany,
            company,
            salutation,
            titel,
            firstName,
            lastName,
            street,
            additionalAddress,
            zipCode,
            city,
            country,
            phoneNumber,
            email,
            password,
        };
        await signup({ user, standortUrl });
        let redirectUrl = `/account`;
        if (context === 'checkout') {
            if (type) {
                redirectUrl = `/initiateCheckout?type=${type}`;
            } else {
                redirectUrl = `/warenkorb`;
            }
        }
        actions.setSubmitting(false);
        return history.push(redirectUrl);
    };

    return (
        <div className="uk-container uk-container-xsmall">
            <Formik
                initialValues={{ ...InitialValues, standortUrl }}
                validationSchema={SignupSchema}
                onSubmit={handleSignupSubmit}
                render={({ values, setFieldValue, errors, touched }) => {
                    return (
                        <Form className="uk-form">
                            <ul data-uk-accordion="multiple: true">
                                <PersonalInformationAccordionItem
                                    setFieldValue={setFieldValue}
                                    errors={errors}
                                    touched={touched}
                                    values={values}
                                />
                                <AccountInformation />
                                <Button fullWidth type="submit">
                                    Registrieren
                                </Button>
                            </ul>
                        </Form>
                    );
                }}
            />
        </div>
    );
}

const mapDispatchToProps = { signup };

export default connect(null, mapDispatchToProps)(RegisterForm);
